import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  adminFetchBtwApplicationById,
  adminArchiveBtwApplication,
  adminDeleteBtwApplication,
  adminFetchBtwStatuses,
  adminFetchAdmins,
  adminFetchBtwLabels,
  adminSetBtwApplicationLabels,
  adminUpdateBtwDeadline,
  adminUpdateBtwWorkflowStatus,
  adminUpdateBtwAssignee,
  adminAddBtwComment,
  adminEditBtwComment,
  adminDeleteBtwComment,
  adminUpdateBtwDetails,
  adminAddBtwFile,
  adminReplaceBtwInvoice,
  adminDeleteBtwInvoice,
  adminDeleteBtwOtherFile,
  adminDownloadBtwMachtigingPdf,
  adminDownloadBelastingdienstFormFile,
  adminConfirmBtwInvoicePaid,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import ConfirmModal from "../../components/confirm-modal";
import {
  batteryUsageLabel,
  genderLabel,
  step2ValueLabel,
} from "../../core/btw-field-labels";
import SendCustomerEmailModal from "../../components/send-customer-email-modal";
import BtwStatusMenu from "../../components/btw-status-menu";
import BtwAssigneeMenu from "../../components/btw-assignee-menu";
import BtwActivityFeed from "../../components/btw-activity-feed";
import BtwLabelPicker from "../../components/btw-label-picker";
import BtwDeadlinePicker from "../../components/btw-deadline-picker";
import BelastingdienstFormModal from "../../components/belastingdienst-form-modal";
import BtwBerekeningSection from "../../components/btw-berekening-section";
import EmailLogList from "../../components/email-log-list";
import EmailPreviewModal from "../../components/email-preview-modal";

const STATUS_LABELS = {
  pending_payment: { label: "Wacht op betaling", color: "bg-[#FFF7D6] text-[#7F5F01]" },
  paid: { label: "Betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  abandoned: { label: "Afgebroken", color: "bg-[#091E420F] text-ink-subtle" },
  refunded: { label: "Terugbetaald", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

// Voor partner-mode aanvragen heeft "Betaald" geen betekenis (klant betaalt
// nooit). Hier mappen we de payment-status om naar partner-context.
const PARTNER_PAYMENT_LABELS = {
  paid: { label: "Aanvraag ontvangen", color: "bg-[#F3F0FF] text-[#5E4DB2]" },
};

const PARTNER_INVOICE_LABELS = {
  pending: { label: "Nog te factureren", color: "bg-[#F3F0FF] text-[#5E4DB2]" },
  invoiced: { label: "Factuur verstuurd", color: "bg-brand-subtle text-brand" },
  paid: { label: "Factuur betaald", color: "bg-[#DCFFF1] text-[#1F845A]" },
  credited: { label: "Gecrediteerd", color: "bg-[#FFECEB] text-[#AE2A19]" },
};

const QUAL_LABELS = {
  qualified: { label: "Geschikt", color: "bg-[#DCFFF1] text-[#1F845A]" },
  disqualified: { label: "Handmatige beoordeling", color: "bg-[#FFF7D6] text-[#7F5F01]" },
  incomplete: { label: "Incompleet", color: "bg-[#091E420F] text-ink-subtle" },
};

const QUALIFICATION_FLAG_LABELS = {
  previous_vat_pre2020_not_sold:
    "Eerder BTW teruggevraagd vóór 2020 - oude woning nog niet verkocht",
  previous_vat_pre2020_no_move:
    "Eerder BTW teruggevraagd vóór 2020 - niet verhuisd",
  entrepreneur_complex_form:
    "Onderneming in complexe rechtsvorm (BV/VOF/maatschap)",
  entrepreneur_no_vat: "Ondernemer zonder BTW-aangifte",
  vat_over_kor_threshold:
    "BTW > € 2.495 - 4 jaar BTW-aangifte vereist (geen directe KOR)",
  vat_under_threshold:
    "BTW onder drempel (oude logica - aanvraag handmatig nakijken)",
};

const STEP2_LABELS = {
  previousVat: "Eerder BTW teruggevraagd",
  previousInstall: "Eerdere installatie",
  moved: "Verhuisd afgelopen jaar",
  oldHouseSold: "Oude woning verkocht",
  hasPartner: "Fiscale partner",
  partnerEntrepreneur: "Partner is BTW-ondernemer",
  partnerFutureEntrepreneur: "Partner wordt ondernemer",
  isEntrepreneur: "Klant is al BTW-ondernemer",
  businessForm: "Bedrijfsvorm",
  futureEntrepreneur: "Klant wordt ondernemer",
  vatThreshold: "Onder BTW-drempel (KOR)",
};

const Badge = ({ map, value }) => {
  const cfg = map[value] ?? { label: value || "-", color: "bg-[#091E420F] text-ink-subtle" };
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const yesNoLabel = (v) => {
  if (v === "ja") return "Ja";
  if (v === "nee") return "Nee";
  if (v === "na" || v === "n.v.t.") return "n.v.t.";
  if (v === undefined || v === null || v === "") return "-";
  return String(v);
};

const formatDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("nl-NL");
};

const formatDateTime = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("nl-NL");
};

const Row = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-line py-2 text-sm last:border-b-0">
    <dt className="col-span-1 text-ink-subtlest">{label}</dt>
    <dd className={`col-span-2 text-ink ${mono ? "break-all text-xs font-medium tabular-nums" : ""}`}>
      {value ?? "-"}
    </dd>
  </div>
);

const EditRow = ({ label, type = "text", value, onChange, options }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-line py-2 text-sm last:border-b-0">
    <label className="col-span-1 pt-1.5 text-xs font-semibold text-ink-subtlest">{label}</label>
    {options ? (
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="col-span-2 rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
      >
        <option value="">-</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="col-span-2 rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
      />
    )}
  </div>
);

const Section = ({
  title,
  children,
  editable = false,
  isEditing = false,
  saving = false,
  onEdit,
  onSave,
  onCancel,
}) => (
  <section className="mb-4 rounded bg-white p-5 shadow-card">
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {editable ? (
        isEditing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annuleer
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
          >
            Bewerken
          </button>
        )
      ) : null}
    </div>
    <dl>{children}</dl>
  </section>
);

const sortActivitiesDesc = (list = []) =>
  [...list].sort(
    (a, b) =>
      new Date(b.createdAt ?? 0).getTime() -
      new Date(a.createdAt ?? 0).getTime(),
  );

const BtwBatterijAanvraagDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromList = Boolean(location.state?.from);
  const { userData } = useUser();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = () => setReloadKey((k) => k + 1);
  const [openEmailLogId, setOpenEmailLogId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [editingSection, setEditingSection] = useState(null); // "step1" | "step3" | null
  const [editDraft, setEditDraft] = useState({});
  const [savingSection, setSavingSection] = useState(false);
  const [fileBusy, setFileBusy] = useState(null); // "invoice" | "addFile" | other-fileId
  const [downloadingMachtiging, setDownloadingMachtiging] = useState(false);
  const [invoicePaidBusy, setInvoicePaidBusy] = useState(false);

  const confirmInvoicePaid = () =>
    askConfirm({
      title: "Betaling bevestigen?",
      message:
        "De klant heeft doorgegeven dat de factuur aan de installateur volledig is betaald. De bevestigings-datum wordt het moment van nu.",
      confirmLabel: "Bevestig betaling",
      onConfirm: async () => {
        const res = await adminConfirmBtwInvoicePaid(id, { confirmed: true });
        setData(res?.data ?? res);
      },
    });

  const undoInvoicePaid = () =>
    askConfirm({
      title: "Betaal-bevestiging ongedaan maken?",
      message:
        "De bevestiging (en een eventueel geupload betaalbewijs) wordt verwijderd van deze aanvraag.",
      confirmLabel: "Ongedaan maken",
      variant: "danger",
      onConfirm: async () => {
        const res = await adminConfirmBtwInvoicePaid(id, { confirmed: false });
        setData(res?.data ?? res);
      },
    });

  const uploadInvoicePaidProof = async (file) => {
    if (!file) return;
    setInvoicePaidBusy(true);
    try {
      const res = await adminConfirmBtwInvoicePaid(id, {
        confirmed: true,
        file,
      });
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? "Uploaden van betaalbewijs mislukt.",
      );
    } finally {
      setInvoicePaidBusy(false);
    }
  };

  // Belastingdienst-formulier ON0202: openen gaat via een modal waarin de
  // waarden eerst gecontroleerd worden.
  const [belastingdienstOpen, setBelastingdienstOpen] = useState(false);
  const [belastingdienstBusy, setBelastingdienstBusy] = useState(false);

  const downloadBelastingdienstFormulier = async () => {
    setBelastingdienstBusy(true);
    try {
      const blob = await adminDownloadBelastingdienstFormFile(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        data?.belastingdienstForm?.file?.originalName ??
        "belastingdienst-formulier.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErrorBanner("Het opgeslagen formulier kon niet opgehaald worden.");
    } finally {
      setBelastingdienstBusy(false);
    }
  };

  const downloadMachtigingPdf = async () => {
    setDownloadingMachtiging(true);
    try {
      const blob = await adminDownloadBtwMachtigingPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `machtiging-${data?.step1?.achternaam ?? id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErrorBanner("Download van machtiging-PDF mislukt.");
    } finally {
      setDownloadingMachtiging(false);
    }
  };
  // Generieke action-confirm modal + foutbanner (vervangt window.confirm/alert).
  const [actionModal, setActionModal] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);

  const askConfirm = (config) => setActionModal(config);
  const runConfirm = async () => {
    if (!actionModal?.onConfirm) return;
    setActionBusy(true);
    try {
      await actionModal.onConfirm();
      setActionModal(null);
    } catch (err) {
      setErrorBanner(
        err?.response?.data?.error ?? err?.message ?? "Actie mislukt.",
      );
    } finally {
      setActionBusy(false);
    }
  };
  const showError = (msg) => setErrorBanner(msg ?? "Er ging iets mis.");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      adminFetchBtwApplicationById(id),
      adminFetchBtwStatuses(),
      adminFetchAdmins(),
      adminFetchBtwLabels(),
    ])
      .then(([appRes, statusRes, adminRes, labelRes]) => {
        setData(appRes?.data ?? appRes);
        setStatuses(statusRes?.data ?? statusRes ?? []);
        setAdmins(adminRes?.data ?? adminRes ?? []);
        setAllLabels(labelRes?.data ?? labelRes ?? []);
      })
      .catch((err) =>
        setError(err?.response?.data?.error ?? err.message ?? "Onbekende fout"),
      )
      .finally(() => setLoading(false));
  }, [id, reloadKey]);

  const activities = useMemo(
    () => sortActivitiesDesc(data?.activities ?? []),
    [data?.activities],
  );

  const toggleArchive = async () => {
    if (!data) return;
    const shouldArchive = !data.isArchived;
    setArchiving(true);
    try {
      const res = await adminArchiveBtwApplication(id, shouldArchive);
      setData(res?.data ?? res);
    } catch (err) {
      console.error("Archiveren mislukt", err);
      showError(err?.response?.data?.error ?? "Archiveren mislukt.");
    } finally {
      setArchiving(false);
      setConfirmOpen(false);
    }
  };

  const runDelete = async () => {
    if (!data) return;
    setDeleting(true);
    try {
      await adminDeleteBtwApplication(id);
      navigate("/panel/admin/btw-batterij-overzicht");
    } catch (err) {
      console.error("Verwijderen mislukt", err);
      showError(err?.response?.data?.error ?? "Verwijderen mislukt.");
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (nextStatus, comment) => {
    try {
      const res = await adminUpdateBtwWorkflowStatus(id, nextStatus._id, comment);
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Status wijzigen mislukt.",
      );
      throw err;
    }
  };

  const handleAssigneeChange = async (nextAdmin, comment) => {
    try {
      const res = await adminUpdateBtwAssignee(
        id,
        nextAdmin ? nextAdmin._id : null,
        comment,
      );
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Toewijzing wijzigen mislukt.",
      );
      throw err;
    }
  };

  const handleDeadlineChange = async ({ deadlineAt, deadlineNote, reminderSchedule }) => {
    try {
      const res = await adminUpdateBtwDeadline(id, {
        deadlineAt,
        deadlineNote,
        reminderSchedule,
      });
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Deadline bijwerken mislukt.",
      );
      throw err;
    }
  };

  const handleLabelsChange = async (nextIds) => {
    try {
      const res = await adminSetBtwApplicationLabels(id, nextIds);
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Labels bijwerken mislukt.",
      );
      throw err;
    }
  };

  const handleAddComment = async (body) => {
    setPostingComment(true);
    try {
      const res = await adminAddBtwComment(id, body);
      const payload = res?.data ?? res;
      setData((prev) =>
        prev
          ? {
              ...prev,
              activities: [...(prev.activities ?? []), payload.activity],
              lastActivityAt: payload.lastActivityAt ?? prev.lastActivityAt,
            }
          : prev,
      );
    } finally {
      setPostingComment(false);
    }
  };

  const handleEditComment = async (commentId, body) => {
    const res = await adminEditBtwComment(id, commentId, body);
    const updated = res?.data ?? res;
    setData((prev) =>
      prev
        ? {
            ...prev,
            activities: (prev.activities ?? []).map((a) =>
              String(a._id) === String(commentId)
                ? { ...a, body: updated.body ?? body, updatedAt: updated.updatedAt, isEdited: true }
                : a,
            ),
          }
        : prev,
    );
  };

  const startEdit = (section, initial) => {
    setEditDraft({ ...initial });
    setEditingSection(section);
  };
  const cancelEdit = () => {
    setEditDraft({});
    setEditingSection(null);
  };
  const setDraftField = (key, value) =>
    setEditDraft((d) => ({ ...d, [key]: value }));

  const saveSection = async (section) => {
    setSavingSection(true);
    try {
      const res = await adminUpdateBtwDetails(id, { [section]: editDraft });
      setData(res?.data ?? res);
      // Berekening teruggave haalt zijn eigen data op; die moet mee-verversen
      // als hier het aankoopbedrag of de capaciteit is aangepast.
      setReloadKey((k) => k + 1);
      cancelEdit();
    } catch (err) {
      showError(err?.response?.data?.error ?? err?.message ?? "Opslaan mislukt.");
    } finally {
      setSavingSection(false);
    }
  };

  const handleReplaceInvoice = async (file) => {
    if (!file) return;
    setFileBusy("invoice");
    try {
      const res = await adminReplaceBtwInvoice(id, file);
      setData(res?.data ?? res);
    } catch (err) {
      showError(err?.response?.data?.error ?? err?.message ?? "Upload mislukt.");
    } finally {
      setFileBusy(null);
    }
  };

  const handleDeleteInvoice = () => {
    askConfirm({
      title: "Factuur verwijderen?",
      message: "De geüploade factuur wordt verwijderd uit deze aanvraag. Dit is niet ongedaan te maken.",
      variant: "danger",
      confirmLabel: "Verwijder factuur",
      onConfirm: async () => {
        setFileBusy("invoice");
        try {
          const res = await adminDeleteBtwInvoice(id);
          setData(res?.data ?? res);
        } finally {
          setFileBusy(null);
        }
      },
    });
  };

  const handleAddFile = async (file) => {
    if (!file) return;
    setFileBusy("addFile");
    try {
      const res = await adminAddBtwFile(id, file);
      setData(res?.data ?? res);
    } catch (err) {
      showError(err?.response?.data?.error ?? err?.message ?? "Upload mislukt.");
    } finally {
      setFileBusy(null);
    }
  };

  const handleDeleteOtherFile = (publicId) => {
    askConfirm({
      title: "Bijlage verwijderen?",
      message: "De bijlage wordt verwijderd. Dit is niet ongedaan te maken.",
      variant: "danger",
      confirmLabel: "Verwijder bijlage",
      onConfirm: async () => {
        setFileBusy(publicId);
        try {
          const res = await adminDeleteBtwOtherFile(id, publicId);
          setData(res?.data ?? res);
        } finally {
          setFileBusy(null);
        }
      },
    });
  };

  const handleDeleteComment = (commentId) => {
    askConfirm({
      title: "Opmerking verwijderen?",
      message: "Je opmerking wordt verwijderd uit de activity feed.",
      variant: "danger",
      confirmLabel: "Verwijder",
      onConfirm: async () => {
        await adminDeleteBtwComment(id, commentId);
        setData((prev) =>
          prev
            ? {
                ...prev,
                activities: (prev.activities ?? []).filter(
                  (a) => String(a._id) !== String(commentId),
                ),
              }
            : prev,
        );
      },
    });
  };

  if (loading) {
    return <div className="page-wrapper px-8 pt-20 pb-12">Laden...</div>;
  }
  if (error) {
    return <div className="page-wrapper px-8 pt-20 pb-12 text-sm text-[#AE2A19]">{error}</div>;
  }
  if (!data) {
    return <div className="page-wrapper px-8 pt-20 pb-12">Niet gevonden.</div>;
  }

  const { step1 = {}, step2 = {}, step3 = {} } = data;
  const fullName = `${step1.voorletters ?? ""} ${step1.tussenvoegsel ?? ""} ${step1.achternaam ?? ""}`
    .replace(/\s+/g, " ")
    .trim();
  const adres = step1.straatnaam
    ? `${step1.straatnaam} ${step1.huisnummer ?? ""}, ${step1.postcode ?? ""} ${step1.plaatsnaam ?? ""}`.trim()
    : null;

  const workflowStatus = data.workflowStatusId ?? null;
  const assignee = data.assignedToAdminId ?? null;

  return (
    <div className="page-wrapper px-8 pt-20 pb-12">
      {errorBanner ? (
        <div className="mb-4 flex items-start justify-between gap-3 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
          <span>{errorBanner}</span>
          <button
            type="button"
            onClick={() => setErrorBanner(null)}
            className="hover:underline"
            aria-label="Sluit foutmelding"
          >
            ✕
          </button>
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
            <span className="mx-1">/</span>
            <button
              type="button"
              onClick={() => {
                // Alleen browser-back als we via de lijst/board hier kwamen - die zet
                // `location.state.from`. Bij een deeplink/refresh ontbreekt state en
                // gaan we naar de overzicht-URL (zonder filter-context terug te knallen
                // naar een onbekende vorige pagina).
                if (cameFromList) navigate(-1);
                else navigate("/panel/admin/btw-batterij-overzicht");
              }}
              className="hover:underline"
            >
              BTW Batterij
            </button>
            <span className="mx-1">/</span>
            <span>{data.issueKey ? data.issueKey : `Aanvraag ${(data._id ?? "").slice(-8)}`}</span>
          </nav>
          <h1 className="flex flex-wrap items-center gap-2 text-2xl font-medium text-ink">
            <span className="truncate">{fullName || "Onbekende aanvrager"}</span>
            {data.isArchived ? (
              <span className="inline-flex items-center rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                Gearchiveerd
              </span>
            ) : null}
          </h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            {data.issueKey ? (
              <span className="mr-1 text-xs font-medium text-ink-subtlest">
                {data.issueKey}
              </span>
            ) : (
              <>Aanvraag <span className="text-xs font-medium">{(data._id ?? "").slice(-8)}</span></>
            )}
            {" · "}
            Ingediend {formatDateTime(data.createdAt)}
            {data.lastActivityAt ? (
              <>
                {" · "}
                Laatste activiteit {formatDateTime(data.lastActivityAt)}
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (cameFromList) navigate(-1);
              else navigate("/panel/admin/btw-batterij-overzicht");
            }}
            className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-brand hover:underline"
          >
            ← Terug naar overzicht
          </button>
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            disabled={archiving || deleting || !step1.email}
            className="inline-flex h-8 items-center gap-1.5 rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            title={!step1.email ? "Geen e-mailadres bekend voor deze klant" : ""}
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            Mail klant
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={archiving || deleting}
            className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink transition hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {data.isArchived ? "Uit archief halen" : "Archiveren"}
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            disabled={archiving || deleting}
            className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-[#AE2A19] transition hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Verwijderen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      {/* Rechterkolom (op desktop): status, toewijzing, deadline, labels */}
      <aside className="order-first lg:order-last">
        <div className="rounded bg-white p-5 shadow-card">
          <h3 className="mb-4 text-base font-semibold text-ink">Details</h3>
          <div className="space-y-5">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                Workflow-status
              </div>
              <p className="mb-2 text-xs text-ink-subtlest">
                Waar in het proces deze aanvraag zit. Eén status tegelijk, lineair.
              </p>
              <BtwStatusMenu
                current={workflowStatus}
                options={statuses}
                onSelect={handleStatusChange}
                align="right"
              />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                Toegewezen aan
              </div>
              <p className="mb-2 text-xs text-ink-subtlest">
                Welke admin deze aanvraag oppakt.
              </p>
              <BtwAssigneeMenu
                current={assignee}
                admins={admins}
                currentUserId={userData?._id}
                onSelect={handleAssigneeChange}
                align="right"
              />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                {data.billingMode === "partner_invoice"
                  ? "Facturering & kwalificatie"
                  : "Betaling & kwalificatie"}
              </div>
              <p className="mb-2 text-xs text-ink-subtlest">
                {data.billingMode === "partner_invoice"
                  ? "Of de partner-factuur is verstuurd / betaald, en of de aanvraag geschikt is."
                  : "Of de klant heeft betaald, en of de aanvraag geschikt is."}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {data.billingMode === "partner_invoice" ? (
                  <Badge
                    map={PARTNER_INVOICE_LABELS}
                    value={data.partnerInvoice?.status ?? "pending"}
                  />
                ) : (
                  <Badge map={STATUS_LABELS} value={data.status} />
                )}
                <Badge map={QUAL_LABELS} value={data.qualificationStatus} />
              </div>
            </div>
            <div className="border-t border-line pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                Deadline
              </div>
              <p className="mb-2 text-xs text-ink-subtlest">
                Trigger reminders naar de toegewezen admin op 7d / 4d / op-dag /
                wekelijks overdue.
              </p>
              <BtwDeadlinePicker
                value={data.deadlineAt}
                note={data.deadlineNote}
                schedule={data.reminderSchedule}
                onSave={handleDeadlineChange}
              />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                Labels
              </div>
              <p className="mb-2 text-xs text-ink-subtlest">
                Vrije tags die je orthogonaal aan de status zet. Meerdere tegelijk
                mogelijk (bv. &laquo;Wacht op klant&raquo; + &laquo;Spoed&raquo;).
              </p>
              <BtwLabelPicker
                selected={(data.labelIds ?? []).map((l) =>
                  typeof l === "object" ? l._id : l,
                )}
                options={allLabels}
                onChange={handleLabelsChange}
                onLabelsChanged={async () => {
                  const res = await adminFetchBtwLabels();
                  setAllLabels(res?.data ?? res ?? []);
                }}
              />
            </div>
            <div className="border-t border-line pt-4 text-xs text-ink-subtlest">
              <div>Ingediend {formatDateTime(data.createdAt)}</div>
              {data.lastActivityAt ? (
                <div className="mt-1">Laatste activiteit {formatDateTime(data.lastActivityAt)}</div>
              ) : null}
            </div>
          </div>
        </div>
      </aside>

      {/* Linkerkolom: de inhoud van de aanvraag */}
      <div className="min-w-0">
      <Section
        title="Aanvrager"
        editable
        isEditing={editingSection === "step1"}
        saving={savingSection}
        onEdit={() => startEdit("step1", step1)}
        onCancel={cancelEdit}
        onSave={() => saveSection("step1")}
      >
        {editingSection === "step1" ? (
          <>
            <EditRow
              label="Voorletters"
              value={editDraft.voorletters}
              onChange={(v) => setDraftField("voorletters", v)}
            />
            <EditRow
              label="Tussenvoegsel"
              value={editDraft.tussenvoegsel}
              onChange={(v) => setDraftField("tussenvoegsel", v)}
            />
            <EditRow
              label="Achternaam"
              value={editDraft.achternaam}
              onChange={(v) => setDraftField("achternaam", v)}
            />
            <EditRow
              label="Geslacht"
              value={editDraft.geslacht}
              onChange={(v) => setDraftField("geslacht", v)}
              options={[
                { value: "man", label: "Man" },
                { value: "vrouw", label: "Vrouw" },
                { value: "anders", label: "Anders" },
              ]}
            />
            <EditRow
              label="E-mail"
              type="email"
              value={editDraft.email}
              onChange={(v) => setDraftField("email", v)}
            />
            <EditRow
              label="Telefoon"
              type="tel"
              value={editDraft.telefoon}
              onChange={(v) => setDraftField("telefoon", v)}
            />
            <EditRow
              label="Postcode"
              value={editDraft.postcode}
              onChange={(v) => setDraftField("postcode", v.toUpperCase())}
            />
            <EditRow
              label="Huisnummer"
              value={editDraft.huisnummer}
              onChange={(v) => setDraftField("huisnummer", v)}
            />
            <EditRow
              label="Straatnaam"
              value={editDraft.straatnaam}
              onChange={(v) => setDraftField("straatnaam", v)}
            />
            <EditRow
              label="Plaatsnaam"
              value={editDraft.plaatsnaam}
              onChange={(v) => setDraftField("plaatsnaam", v)}
            />
          </>
        ) : (
          <>
            <Row label="Naam" value={fullName || "-"} />
            <Row label="Geslacht" value={genderLabel(step1.geslacht)} />
            <Row label="E-mail" value={step1.email} />
            <Row label="Telefoon" value={step1.telefoon} />
            <Row label="Adres" value={adres ?? "-"} />
          </>
        )}
      </Section>

      <Section
        title="Gegevens voor BTW-teruggave"
        editable
        isEditing={editingSection === "step3"}
        saving={savingSection}
        onEdit={() => startEdit("step3", step3)}
        onCancel={cancelEdit}
        onSave={() => saveSection("step3")}
      >
        {editingSection === "step3" ? (
          <>
            <EditRow
              label="BSN"
              value={editDraft.bsn}
              onChange={(v) => setDraftField("bsn", v.replace(/\D/g, ""))}
            />
            <EditRow
              label="Geboortedatum"
              type="date"
              value={(editDraft.geboortedatum ?? "").slice(0, 10)}
              onChange={(v) => setDraftField("geboortedatum", v)}
            />
            <EditRow
              label="IBAN"
              value={editDraft.iban}
              onChange={(v) => setDraftField("iban", v.toUpperCase().replace(/\s/g, ""))}
            />
            <EditRow
              label="Tenaamstelling IBAN"
              value={editDraft.ibanHolder}
              onChange={(v) => setDraftField("ibanHolder", v)}
            />
            <EditRow
              label="Factuurdatum"
              type="date"
              value={(editDraft.invoiceDate ?? "").slice(0, 10)}
              onChange={(v) => setDraftField("invoiceDate", v)}
            />
            <EditRow
              label="Installatiedatum"
              type="date"
              value={(editDraft.installationDate ?? "").slice(0, 10)}
              onChange={(v) => setDraftField("installationDate", v)}
            />
            <EditRow
              label="Capaciteit (kWh)"
              type="number"
              value={editDraft.batteryCapacityKwh}
              onChange={(v) => setDraftField("batteryCapacityKwh", v)}
            />
            <EditRow
              label="Aankoopbedrag (incl. btw)"
              type="number"
              value={editDraft.purchaseAmount}
              onChange={(v) => setDraftField("purchaseAmount", v)}
            />
            <EditRow
              label="Gebruik batterij"
              value={editDraft.batteryUsage}
              onChange={(v) => setDraftField("batteryUsage", v)}
              options={[
                { value: "trading", label: "Energie handel" },
                { value: "zelfgebruik", label: "Eigen verbruik" },
                { value: "beide", label: "Beide (eigen verbruik + handel)" },
                { value: "verhuurd", label: "Verhuurd aan energiemaatschappij" },
              ]}
            />
            <EditRow
              label="Dynamisch energiecontract"
              value={editDraft.dynamicEnergyContract}
              onChange={(v) => setDraftField("dynamicEnergyContract", v)}
              options={[
                { value: "ja", label: "Ja" },
                { value: "nee", label: "Nee" },
              ]}
            />
            {editDraft.dynamicEnergyContract === "ja" ? (
              <>
                <EditRow
                  label="Dyn. contract op naam aanvrager"
                  value={editDraft.dynamicContractInName}
                  onChange={(v) => setDraftField("dynamicContractInName", v)}
                  options={[
                    { value: "ja", label: "Ja" },
                    { value: "nee", label: "Nee" },
                  ]}
                />
                <EditRow
                  label="Energiecontract op naam aanvrager"
                  value={editDraft.energyContractInName}
                  onChange={(v) => setDraftField("energyContractInName", v)}
                  options={[
                    { value: "ja", label: "Ja" },
                    { value: "nee", label: "Nee" },
                  ]}
                />
                <EditRow
                  label="Afsluitdatum dyn. contract"
                  type="date"
                  value={(editDraft.dynamicContractSignDate ?? "").slice(0, 10)}
                  onChange={(v) => setDraftField("dynamicContractSignDate", v)}
                />
                <EditRow
                  label="Ingangsdatum contract"
                  type="date"
                  value={(editDraft.dynamicContractStartDate ?? "").slice(0, 10)}
                  onChange={(v) => setDraftField("dynamicContractStartDate", v)}
                />
                <p className="py-2 text-xs text-ink-subtlest">
                  Ligt de ingangsdatum na de installatiedatum, dan telt de
                  afdracht over het eerste jaar vanaf de ingangsdatum.
                </p>
              </>
            ) : null}
            <EditRow
              label="Opmerking klant"
              value={editDraft.comments}
              onChange={(v) => setDraftField("comments", v)}
            />
          </>
        ) : (
          <>
            <Row label="BSN" value={step3.bsn} mono />
            <Row label="Geboortedatum" value={formatDate(step3.geboortedatum)} />
            <Row label="IBAN" value={step3.iban} mono />
            <Row label="Tenaamstelling IBAN" value={step3.ibanHolder} />
            <Row label="Factuurdatum" value={formatDate(step3.invoiceDate)} />
            <Row label="Installatiedatum" value={formatDate(step3.installationDate)} />
            <Row label="Capaciteit batterij" value={step3.batteryCapacityKwh ? `${step3.batteryCapacityKwh} kWh` : "-"} />
            <Row
              label="Aankoopbedrag (incl. btw)"
              value={
                step3.purchaseAmount
                  ? `€ ${Number(step3.purchaseAmount).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-"
              }
            />
            <Row
              label="Gebruik batterij"
              value={batteryUsageLabel(step3.batteryUsage)}
            />
            <Row
              label="Dynamisch energiecontract"
              value={yesNoLabel(step3.dynamicEnergyContract)}
            />
            {step3.dynamicEnergyContract === "ja" ? (
              <>
                <Row
                  label="Dyn. contract op naam aanvrager"
                  value={yesNoLabel(step3.dynamicContractInName)}
                />
                <Row
                  label="Energiecontract op naam aanvrager"
                  value={yesNoLabel(step3.energyContractInName)}
                />
                <Row
                  label="Afsluitdatum dyn. contract"
                  value={formatDate(step3.dynamicContractSignDate)}
                />
                <Row
                  label="Ingangsdatum contract"
                  value={formatDate(step3.dynamicContractStartDate)}
                />
              </>
            ) : null}
          </>
        )}
      </Section>

      <Section title="Kwalificatie-antwoorden">
        {Object.entries(step2).length === 0 ? (
          <div className="py-2 text-sm text-ink-subtlest">Geen antwoorden opgeslagen.</div>
        ) : (
          Object.entries(step2).map(([k, v]) => (
            <Row
              key={k}
              label={STEP2_LABELS[k] ?? k}
              value={step2ValueLabel(k, v)}
            />
          ))
        )}
        {data.qualificationFlag ? (
          <Row
            label="Vlag"
            value={QUALIFICATION_FLAG_LABELS[data.qualificationFlag] ?? data.qualificationFlag}
          />
        ) : null}
        {data.qualificationMessage ? (
          <Row label="Opmerking systeem" value={data.qualificationMessage} />
        ) : null}
      </Section>

      <Section title="Documenten">
        <div className="space-y-3 py-1">
          {/* Factuur */}
          <div className="rounded bg-sunken px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                  Factuur batterij
                </p>
                {data.invoiceFile ? (
                  <a
                    href={data.invoiceFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block truncate text-sm font-medium text-brand hover:underline"
                  >
                    {data.invoiceFile.originalName}{" "}
                    <span className="text-xs text-ink-subtlest">
                      ({Math.round(data.invoiceFile.bytes / 1024)} KB)
                    </span>
                  </a>
                ) : (
                  <p className="mt-1 text-sm italic text-ink-subtlest">
                    Niet geüpload
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex h-8 cursor-pointer items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
                  {data.invoiceFile ? "Vervangen" : "Uploaden"}
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      handleReplaceInvoice(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                    disabled={fileBusy === "invoice"}
                  />
                </label>
                {data.invoiceFile ? (
                  <button
                    type="button"
                    onClick={handleDeleteInvoice}
                    disabled={fileBusy === "invoice"}
                    className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Verwijderen
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Bijlagen */}
          {(data.otherFiles ?? []).map((f, i) => (
            <div
              key={f.publicId ?? i}
              className="rounded border border-line bg-white px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                    Bijlage {i + 1}
                  </p>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block truncate text-sm font-medium text-brand hover:underline"
                  >
                    {f.originalName}{" "}
                    <span className="text-xs text-ink-subtlest">
                      ({Math.round(f.bytes / 1024)} KB)
                    </span>
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteOtherFile(f.publicId)}
                  disabled={fileBusy === f.publicId}
                  className="inline-flex h-8 items-center rounded px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Verwijderen
                </button>
              </div>
            </div>
          ))}

          {/* Voeg toe */}
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded border border-dashed border-line bg-white px-4 py-3 text-sm font-medium text-ink-subtle hover:border-brand hover:text-brand">
            {fileBusy === "addFile" ? (
              <span>Bezig met uploaden…</span>
            ) : (
              <>
                <span>+</span>
                <span>Bijlage toevoegen</span>
              </>
            )}
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                handleAddFile(e.target.files?.[0]);
                e.target.value = "";
              }}
              disabled={fileBusy === "addFile"}
            />
          </label>
        </div>
      </Section>

      {data.billingMode === "partner_invoice" ? (
        <Section title="Facturering (partner)">
          <Row
            label="Partner"
            value={
              data.partnerUserId?.bedrijfsnaam ??
              data.partnerUserId?.naam ??
              "-"
            }
          />
          <Row
            label="Factuur-status"
            value={
              <Badge
                map={PARTNER_INVOICE_LABELS}
                value={data.partnerInvoice?.status ?? "pending"}
              />
            }
          />
          {data.partnerInvoice?.invoiceId?.invoiceNumber ? (
            <Row
              label="Factuurnummer"
              value={data.partnerInvoice.invoiceId.invoiceNumber}
              mono
            />
          ) : null}
          {data.partnerInvoice?.invoicedAt ? (
            <Row label="Gefactureerd op" value={formatDateTime(data.partnerInvoice.invoicedAt)} />
          ) : null}
          {data.partnerInvoice?.paidAt ? (
            <Row label="Factuur betaald op" value={formatDateTime(data.partnerInvoice.paidAt)} />
          ) : null}
          <Row
            label="Klant betaalde"
            value="€ 0,00 (partner-flow)"
          />
        </Section>
      ) : null}

      <BtwBerekeningSection applicationId={id} reloadKey={reloadKey} />

      {/* Alle mail die het systeem voor dit dossier heeft verstuurd (klant,
          partner en intern), met de inhoud als preview. Bron: EmailLogs. */}
      <Section title="Verstuurde mails">
        <EmailLogList
          applicationId={id}
          applicationModel="BtwApplications"
          compact
          limit={10}
          reloadKey={reloadKey}
          onOpen={setOpenEmailLogId}
        />
      </Section>
      <EmailPreviewModal
        isOpen={!!openEmailLogId}
        emailLogId={openEmailLogId}
        onClose={() => setOpenEmailLogId(null)}
      />

      {/* Alleen zinvol bij een betaalde aanvraag: daarvoor is er nog niets aan
          te vragen bij de Belastingdienst. */}
      {data.status === "paid" ? (
      <Section title="Belastingdienst-formulier">
        <Row
          label="Opgaaf startende onderneming"
          value={
            <div>
              <button
                type="button"
                onClick={() => setBelastingdienstOpen(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
              >
                Formulier voorbereiden
              </button>
              <p className="mt-2 text-xs text-ink-subtlest">
                Vult het originele ON0202-formulier van de Belastingdienst in met
                de gegevens uit deze aanvraag. Je controleert de waarden voordat
                de PDF gemaakt wordt.
              </p>
            </div>
          }
        />
        {data.belastingdienstForm?.file ? (
          <Row
            label="Opgeslagen formulier"
            value={
              <div>
                <button
                  type="button"
                  onClick={downloadBelastingdienstFormulier}
                  disabled={belastingdienstBusy}
                  className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {belastingdienstBusy
                    ? "Ophalen…"
                    : data.belastingdienstForm.file.originalName ??
                      "Download PDF"}
                </button>
                <p className="mt-2 text-xs text-ink-subtlest">
                  Gemaakt op{" "}
                  {formatDateTime(data.belastingdienstForm.generatedAt)}
                  {data.belastingdienstForm.generatedBy
                    ? ` door ${data.belastingdienstForm.generatedBy}`
                    : ""}
                  . Het bestand staat afgeschermd opgeslagen en is alleen via
                  deze knop op te halen.
                </p>
              </div>
            }
          />
        ) : null}
      </Section>
      ) : null}

      {data.machtiging ? (
        <Section title="Akkoord & handtekening">
          <Row
            label="Machtiging BTW-teruggave"
            value={
              data.machtiging.signedAt ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center rounded bg-[#DCFFF1] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#1F845A]">
                    Akkoord gegeven
                  </span>
                  op {formatDateTime(data.machtiging.signedAt)}
                </span>
              ) : (
                "-"
              )
            }
          />
          <Row
            label="Betaling factuur installateur"
            value={
              data.machtiging.invoicePaidConfirmed ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded bg-[#DCFFF1] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#1F845A]">
                    {data.machtiging.invoicePaidConfirmedBy === "admin"
                      ? "Bevestigd via ons"
                      : "Bevestigd door klant"}
                  </span>
                  {data.machtiging.invoicePaidConfirmedAt ? (
                    <span>
                      klant gaf op{" "}
                      {formatDateTime(data.machtiging.invoicePaidConfirmedAt)}{" "}
                      {data.machtiging.invoicePaidConfirmedBy === "admin"
                        ? "aan ons door dat de factuur volledig betaald is"
                        : "akkoord dat de factuur volledig betaald is"}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={undoInvoicePaid}
                    className="text-xs text-ink-subtlest underline hover:text-[#AE2A19]"
                  >
                    Ongedaan maken
                  </button>
                </span>
              ) : (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded bg-[#FFF7D6] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#7F5F01]">
                    Nog niet bevestigd
                  </span>
                  <span className="text-ink-subtlest">
                    de aanvraag wordt pas in behandeling genomen na betaling
                  </span>
                  <button
                    type="button"
                    onClick={confirmInvoicePaid}
                    disabled={invoicePaidBusy}
                    className="inline-flex h-8 items-center rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Markeer als betaald
                  </button>
                </span>
              )
            }
          />
          <Row
            label="Betaalbewijs"
            value={
              <span className="inline-flex flex-wrap items-center gap-3">
                {data.machtiging.invoicePaidProofFile?.url ? (
                  <a
                    href={data.machtiging.invoicePaidProofFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand underline hover:text-brand-pressed"
                  >
                    {data.machtiging.invoicePaidProofFile.originalName ??
                      "Bekijk betaalbewijs"}
                  </a>
                ) : (
                  <span className="text-ink-subtlest">Geen bewijs geupload</span>
                )}
                <label className="inline-flex h-8 cursor-pointer items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
                  {invoicePaidBusy
                    ? "Uploaden…"
                    : data.machtiging.invoicePaidProofFile?.url
                    ? "Vervangen"
                    : "Bewijs uploaden"}
                  <input
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/*"
                    disabled={invoicePaidBusy}
                    onChange={(e) => {
                      uploadInvoicePaidProof(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
              </span>
            }
          />
          {data.machtiging.signatureFile?.url ? (
            <Row
              label="Handtekening"
              value={
                <a
                  href={data.machtiging.signatureFile.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Open handtekening in nieuw tabblad"
                >
                  <img
                    src={data.machtiging.signatureFile.url}
                    alt="Handtekening klant"
                    className="h-20 w-auto max-w-[260px] rounded border border-line bg-white object-contain px-3 py-1.5"
                  />
                </a>
              }
            />
          ) : null}
          <Row
            label="Bewijs van machtiging"
            value={
              <button
                type="button"
                onClick={downloadMachtigingPdf}
                disabled={downloadingMachtiging}
                className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloadingMachtiging ? "Genereren…" : "Download PDF"}
              </button>
            }
          />
        </Section>
      ) : null}

      {data.billingMode !== "partner_invoice" ? (
        <Section title="Betaling">
          <Row label="Status" value={<Badge map={STATUS_LABELS} value={data.status} />} />
          <Row
            label="Bedrag"
            value={`€ ${(data.amountInclVat ?? 0).toFixed(2)} incl. btw (€ ${(data.amountExclVat ?? 0).toFixed(2)} excl. btw)`}
          />
          <Row label="Betaald op" value={formatDateTime(data.paidAt)} />
          <Row label="Stripe sessie" value={data.stripeSessionId} mono />
        </Section>
      ) : null}

      {(step3.comments || data.installateur) ? (
        <Section title="Notities & herkomst">
          {step3.comments ? (
            <Row label="Opmerking klant" value={step3.comments} />
          ) : null}
          {data.partnerUserId ? (
            <Row
              label="Installateur"
              value={
                data.partnerUserId.bedrijfsnaam ??
                data.partnerUserId.naam ??
                data.installateur
              }
            />
          ) : data.installateur ? (
            <Row label="Installateur" value={data.installateur} mono />
          ) : null}
        </Section>
      ) : null}

      <BtwActivityFeed
        activities={activities}
        onAddComment={handleAddComment}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        posting={postingComment}
        currentUserId={userData?._id}
        currentUserPhoto={userData?.profielFoto}
        statuses={statuses}
        admins={admins}
      />
      </div>
      </div>

      <SendCustomerEmailModal
        isOpen={emailOpen}
        applicationId={id}
        customerName={`${step1.voorletters ?? ""} ${step1.tussenvoegsel ?? ""} ${step1.achternaam ?? ""}`
          .replace(/\s+/g, " ")
          .trim() || "deze klant"}
        customerEmail={step1.email ?? ""}
        onClose={() => setEmailOpen(false)}
        onSent={() => refresh()}
      />

      <BelastingdienstFormModal
        isOpen={belastingdienstOpen}
        applicationId={id}
        onClose={() => setBelastingdienstOpen(false)}
        // Alleen verversen als er echt een formulier gemaakt is: dat zet een
        // regel in de activiteitenfeed. Bij annuleren is een refetch verspilling.
        onGenerated={() => refresh()}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title={data.isArchived ? "Uit archief halen?" : "Aanvraag archiveren?"}
        message={
          data.isArchived
            ? "Deze aanvraag wordt teruggezet naar het actieve overzicht."
            : "Deze aanvraag wordt gearchiveerd en verdwijnt uit het actieve overzicht. Je kunt 'm later terughalen via het Archief-tabblad."
        }
        confirmLabel={data.isArchived ? "Uit archief halen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={archiving}
        onConfirm={toggleArchive}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={deleteOpen}
        variant="danger"
        title="Aanvraag definitief verwijderen?"
        message="Deze aanvraag verdwijnt volledig uit de admin. Niet meer terug te halen via de interface. Stripe-betaling blijft in het Stripe Dashboard beschikbaar."
        confirmLabel="Verwijder definitief"
        cancelLabel="Annuleren"
        busy={deleting}
        onConfirm={runDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      {/* Generic action-confirm modal (file deletes, comment deletes etc.) */}
      <ConfirmModal
        isOpen={Boolean(actionModal)}
        variant={actionModal?.variant ?? "primary"}
        title={actionModal?.title ?? "Bevestigen?"}
        message={actionModal?.message ?? ""}
        confirmLabel={actionModal?.confirmLabel ?? "Bevestigen"}
        cancelLabel={actionModal?.cancelLabel ?? "Annuleren"}
        busy={actionBusy}
        onConfirm={runConfirm}
        onCancel={() => {
          if (!actionBusy) setActionModal(null);
        }}
      />
    </div>
  );
};

export default BtwBatterijAanvraagDetailsPage;
